import { useQuery } from '@tanstack/react-query';
import { PLAN_RAMP } from '../../components/charts';
import { adminApi, formatCount, formatMoney, type PricingPlan } from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState } from './AdminLayout';

const FEATURE_LABELS: Record<keyof PricingPlan['features'], string> = {
  llm_configuration: 'LLM configuration',
  security_baseline_harness: 'Security baseline harness',
  security_compliance_qa: 'Security compliance QA',
  waveqa_self_adaptation: 'Self-adaptation',
  limit_llm_calls: 'LLM call limiting',
  separate_qa_reports: 'Separate QA reports',
  customer_support: 'Customer support',
};

/**
 * The plan catalogue, with live occupancy.
 *
 * **Read-only, and that is the design rather than a missing feature.** Prices
 * and limits live in the service configuration, which is what the entitlement
 * checks read and what the payment provider is configured against. An Edit
 * button here would change the figure on this screen and nothing about what a
 * customer is actually charged or allowed — two sources of truth, silently
 * disagreeing, with this one looking authoritative.
 *
 * The screen says so in place of the button, so nobody spends an afternoon
 * looking for where the plans are edited.
 */
export function AdminPricingPlans() {
  usePageTitle('Pricing plans — WaveQA Admin');

  const plans = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => adminApi.getPlans(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Pricing plans</h1>
        <p className="mt-1 text-sm text-hull-400">
          What each plan includes, and how many accounts are on it.
        </p>
      </div>

      <AdminPanelState loading={plans.isPending} error={plans.error}>
        {plans.data && (
          <div className="space-y-6">
            <p className="rounded-card border border-hull-700 bg-hull-800/60 px-4 py-3 text-sm leading-relaxed text-hull-300">
              {plans.data.editing_note}
            </p>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {plans.data.plans.map((plan, index) => (
                <article
                  key={plan.plan}
                  className="flex flex-col rounded-card border border-hull-700 bg-hull-800/60 p-5"
                >
                  <div
                    aria-hidden="true"
                    className="mb-4 h-1 w-10 rounded-full"
                    style={{ background: PLAN_RAMP[index % PLAN_RAMP.length] }}
                  />

                  <h2 className="text-base font-semibold text-white">{plan.display_name}</h2>

                  <p className="mt-1 text-2xl font-semibold tabular-nums text-white">
                    {plan.monthly_price_minor_units === null
                      ? 'Custom'
                      : formatMoney(plan.monthly_price_minor_units, plan.currency, {
                          exact: true,
                        })}
                    {plan.monthly_price_minor_units !== null && (
                      <span className="text-sm font-normal text-hull-400"> / month</span>
                    )}
                  </p>

                  <p className="mt-3 text-sm text-hull-300">
                    {formatCount(plan.subscribers)} account
                    {plan.subscribers === 1 ? '' : 's'}
                    {plan.billing_subscribers !== plan.subscribers && (
                      <span className="text-hull-500">
                        {' '}
                        ({formatCount(plan.billing_subscribers)} billing)
                      </span>
                    )}
                  </p>

                  {!plan.purchasable && (
                    // A paid plan with no configured price id cannot be checked
                    // out. Worth seeing here rather than hearing from a customer.
                    <p className="mt-3 rounded-lg border border-warn-600/40 bg-warn-600/10 px-2.5 py-1.5 text-xs text-warn-400">
                      Not purchasable — no price configured with the billing provider.
                    </p>
                  )}

                  <dl className="mt-4 space-y-1.5 border-t border-hull-700 pt-4 text-xs">
                    <div className="flex justify-between gap-2">
                      <dt className="text-hull-400">Agents</dt>
                      <dd className="tabular-nums text-hull-200">{plan.agent_limit}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-hull-400">Hours / month</dt>
                      <dd className="tabular-nums text-hull-200">
                        {plan.unlimited_hours ? 'Unlimited' : plan.monthly_hours}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-hull-400">Criteria / month</dt>
                      <dd className="tabular-nums text-hull-200">
                        {plan.monthly_criteria ?? 'Unlimited'}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-hull-400">Projects</dt>
                      <dd className="tabular-nums text-hull-200">
                        {plan.max_projects ?? 'Unlimited'}
                      </dd>
                    </div>
                    {plan.localhost_only && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-hull-400">Targets</dt>
                        <dd className="text-hull-200">Localhost only</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <dt className="text-hull-400">Monthly revenue</dt>
                      <dd className="tabular-nums text-hull-200">
                        {formatMoney(plan.monthly_revenue_minor_units, plan.currency, {
                          exact: true,
                        })}
                      </dd>
                    </div>
                  </dl>

                  <ul className="mt-4 space-y-1 border-t border-hull-700 pt-4 text-xs">
                    {(
                      Object.keys(FEATURE_LABELS) as (keyof PricingPlan['features'])[]
                    ).map((feature) => (
                      <li
                        key={feature}
                        className={
                          plan.features[feature] ? 'text-hull-200' : 'text-hull-500'
                        }
                      >
                        <span aria-hidden="true" className="mr-1.5">
                          {plan.features[feature] ? '✓' : '·'}
                        </span>
                        {FEATURE_LABELS[feature]}
                        <span className="sr-only">
                          {plan.features[feature] ? ' included' : ' not included'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        )}
      </AdminPanelState>
    </div>
  );
}
